import { useEffect, type RefObject } from 'react';

interface AutoScrollOptions {
  /** Quiet beat before the first movement, so the reader sees the top first. */
  startDelayMs?: number;
  /** Drift speed. Slow enough to read along with, fast enough to reach the end. */
  pxPerSecond?: number;
  /** Pause once the end is reached, before rewinding. */
  bottomHoldMs?: number;
  /** Duration of the eased rewind back to the top. */
  rewindMs?: number;
  /** Pause at the top before the next pass. */
  topHoldMs?: number;
}

/**
 * Slow looping auto-scroll for a single overflowing element.
 *
 * Written as a real scroll rather than a CSS transform so the element keeps a
 * working native scrollbar: the reader can grab it, wheel it, or tab to it and
 * use the keyboard at any point, and the loop simply picks up from wherever
 * they left it.
 *
 * Pauses whenever a pointer is over the box or focus is inside it, so it never
 * yanks the text away from someone mid-sentence.
 *
 * Honours prefers-reduced-motion by not animating at all; the element stays
 * manually scrollable, which is the accessible outcome rather than a degraded
 * one.
 */
export default function useAutoScroll(
  ref: RefObject<HTMLElement | null>,
  {
    startDelayMs = 1600,
    pxPerSecond = 26,
    bottomHoldMs = 1800,
    rewindMs = 900,
    topHoldMs = 900,
  }: AutoScrollOptions = {}
) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let raf = 0;
    let paused = false;
    let onScreen = false;
    let phase: 'wait' | 'down' | 'hold' | 'rewind' = 'wait';
    let last = performance.now();
    let phaseUntil = last + startDelayMs;
    let rewindFrom = 0;
    let rewindStart = 0;

    /* Sub-pixel accumulator. At this speed a frame advances ~0.4px, and reading
       scrollTop back after assigning a fraction loses it — the position would
       reset to 0 every frame and never move. Integrate here, assign to the DOM. */
    let pos = el.scrollTop;

    const tick = (now: number) => {
      // Clamp dt so returning to a backgrounded tab does not jump the content.
      const dt = Math.min(now - last, 100);
      last = now;

      const max = Math.max(0, el.scrollHeight - el.clientHeight);

      if (!paused && max > 0) {
        if (phase === 'wait') {
          if (now >= phaseUntil) phase = 'down';
        } else if (phase === 'down') {
          pos = Math.min(max, pos + (pxPerSecond * dt) / 1000);
          el.scrollTop = pos;
          if (pos >= max - 0.5) {
            phase = 'hold';
            phaseUntil = now + bottomHoldMs;
          }
        } else if (phase === 'hold') {
          if (now >= phaseUntil) {
            phase = 'rewind';
            rewindFrom = pos;
            rewindStart = now;
          }
        } else {
          const t = Math.min(1, (now - rewindStart) / rewindMs);
          pos = rewindFrom * Math.pow(1 - t, 3);
          el.scrollTop = pos;
          if (t >= 1) {
            phase = 'wait';
            phaseUntil = now + topHoldMs;
          }
        }
      }

      raf = requestAnimationFrame(tick);
    };

    const pause = () => {
      paused = true;
    };

    /* Resume from wherever the reader left the scroll position rather than
       snapping back — with a short beat so it does not lurch the moment the
       pointer clears the edge. */
    const resume = () => {
      paused = false;
      last = performance.now();
      // Adopt wherever the reader left it, so the accumulator does not snap
      // the panel back to its own stale position.
      pos = el.scrollTop;
      phase = 'wait';
      phaseUntil = last + 900;
    };

    el.addEventListener('pointerenter', pause);
    el.addEventListener('pointerleave', resume);
    el.addEventListener('focusin', pause);
    el.addEventListener('focusout', resume);

    /* Run the rAF loop ONLY while the panel is on screen. An ungated loop keeps
       waking the main thread ~60x/sec for a component the visitor may have
       scrolled past minutes ago — measurable battery cost for zero benefit.
       The observer also restarts the sequence from the top on re-entry, so a
       returning reader sees the summary from the beginning. */
    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (!entry || entry.isIntersecting === onScreen) return;
        onScreen = entry.isIntersecting;
        if (onScreen) {
          last = performance.now();
          pos = el.scrollTop;
          phase = 'wait';
          phaseUntil = last + startDelayMs;
          raf = requestAnimationFrame(tick);
        } else {
          cancelAnimationFrame(raf);
          raf = 0;
        }
      },
      { threshold: 0.15 }
    );
    io.observe(el);

    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
      el.removeEventListener('pointerenter', pause);
      el.removeEventListener('pointerleave', resume);
      el.removeEventListener('focusin', pause);
      el.removeEventListener('focusout', resume);
    };
  }, [ref, startDelayMs, pxPerSecond, bottomHoldMs, rewindMs, topHoldMs]);
}
